import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  TextArea,
  Popup,
  Button,
  Confirm,
  Image,
} from "semantic-ui-react";
import { ENV, buildStorageUrl } from "../../../../../../../utils";
import { useDropzone } from "react-dropzone";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Sev02 = React.memo((props) => {
  const { index, slideId, onLetterByOrder, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [time, setTime] = useState("");
  const [question, setQuestion] = useState("");
  const [image, setImage] = useState("");
  const [choices, setChoices] = useState([]);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Updates component state based on the current slide's content in an active session.
   *
   * This `useEffect` hook triggers when the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide matching the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with this content. Specifically, it sets the `time`, `question`,
   * `choices`, and `image` states to the values found in the slide's content.
   *
   * This ensures the component reflects the current slide's information, providing an accurate and
   * interactive editing experience for the session.
   *
   * @dependency slideId - The ID of the slide currently being edited, whose change triggers the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTime(slide.content.time);
      setQuestion(slide.content.question);
      setChoices(slide.content.choices);
      setImage(slide.content.image);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide in the `slidesActiveSessionEdit` array.
   *
   * This useEffect hook is triggered whenever there are changes to the `time`, `question`, or `image`
   * states. It first finds the index of the slide with the matching `slideId` in the
   * `slidesActiveSessionEdit` array. If the slide is found (i.e., `slideIndex` is not -1), the hook
   * updates the slide's content with the new `time`, `image`, and `question` values.
   *
   * This ensures that the slide's content in the active session edit is kept up-to-date with the
   * latest changes made by the user, allowing for real-time updates and edits to the session's slides.
   *
   * @dependency time - The current time value for the slide.
   * @dependency question - The current question content for the slide.
   * @dependency image - The current image associated with the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.time = time;
      slidesActiveSessionEdit[slideIndex].content.image = image;
      slidesActiveSessionEdit[slideIndex].content.question = question;
    }
  }, [time, question, image]);

  /**
   * Handles various input changes and file drops for a session editing component.
   *
   * `handleTimeChange`:
   * - Updates the `time` state based on the input value. This function is typically used in a time input field.
   *
   * `handleQuestionChange`:
   * - Updates the `question` state, trimming any leading whitespace from the input value. This ensures that the question text starts immediately without any leading spaces.
   *
   * `handleChoiceChange`:
   * - Updates a specific choice's `answer` within the `choices` array. It takes an `index` to identify which choice to update and the `event` object to access the new value. The choice's answer is updated with leading whitespace removed.
   *
   * `onDrop`:
   * - A callback function for the dropzone that handles file drops. It updates the `image` state with a URL representing the first accepted file. This function is designed to work with images, creating a URL that can be used to display the dropped image.
   *
   * `useDropzone` configuration:
   * - Configures the dropzone to accept JPEG and PNG images. It uses the `onDrop` callback to handle file drops.
   *
   * These functions and the dropzone configuration are part of the component's logic to manage session content, including time, questions, choices, and images.
   */
  const handleTimeChange = (event) => {
    setTime(event.target.value);
  };

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value.trimStart());
  };

  const handleChoiceChange = (index, event) => {
    const newChoices = [...choices];
    newChoices[index].answer = event.target.value.trimStart();
    setChoices(newChoices);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    onDrop,
  });

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__time">
          <div className="media-slide__label-div">
            <Icon name="clock outline" />
            <h5 className="media-slide__label">Duración:</h5>
          </div>
          <Input
            className="media-slide__input-time"
            labelPosition="right"
            type="number"
            min="1"
            name="time"
            value={time}
            onChange={handleTimeChange}
          >
            <input />
            <Label className="media-slide__input-time-label">minuto(s)</Label>
          </Input>
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="question" />
            <h5 className="media-slide__label">Pregunta:</h5>
          </div>
          <TextArea
            className="media-slide__textarea"
            rows={5}
            name="question"
            value={question}
            onChange={handleQuestionChange}
          />
          <div className="media-slide__image" {...getRootProps()}>
            <input {...getInputProps()} />
            <Image
              src={image?.startsWith('blob:') ? image : (buildStorageUrl(image) || image)}
              rounded
              fluid
            />
          </div>
          <div>
            {choices.map((choice, choiceIndex) => (
              <div className="col-md-12 mb-1" key={choiceIndex}>
                <div className="input-group border">
                  <Input
                    className="media-slide__input-choice"
                    labelPosition="left"
                    value={choice.answer}
                    onChange={(e) => handleChoiceChange(choiceIndex, e)}
                  >
                    <Label
                      className={`media-slide__input-label${
                        choice.correct ? "-success" : ""
                      }`}
                    >
                      {onLetterByOrder(choiceIndex + 1)}.
                    </Label>
                    <input />
                  </Input>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
